import {
  Box,
  Icon,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';

import type { Branch } from '../utils/Branch';
import SelectionCardsSkeleton from './SelectionCardsSkeleton';

type BranchesSelectionProps = {
  branches: Branch[];
  selectedBranch: Partial<Branch> | null;
  onSelectBranch: (branch: Branch) => void;
  isLoading?: boolean;
};

const BranchesSelection: React.FC<BranchesSelectionProps> = ({
  branches,
  selectedBranch,
  onSelectBranch,
  isLoading = false,
}) => {
  return (
    <Stack
      direction='column'
      spacing={2}
      data-testid={
        isLoading
          ? 'branchesSelectionContainerLoading'
          : 'branchesSelectionContainer'
      }
      sx={{
        width: '100%',
        my: 2,
        px: 2,
        overflowY: 'scroll',
        scrollbarColor: 'grey transparent',
      }}
    >
      {isLoading ? (
        <SelectionCardsSkeleton count={4} />
      ) : (
        branches.map((branch) => {
          const isSelected = selectedBranch?.branchId === branch.branchId;

          return (
            <Stack
              direction='row'
              key={branch.branchId}
              onClick={() => onSelectBranch(branch)}
              sx={[
                {
                  backgroundColor: 'background.default',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2,
                  padding: 2,
                  width: '100%',
                  alignItems: 'center',
                  cursor: 'pointer',
                  height: 128,
                },
                isSelected && {
                  backgroundColor: '#FCFBF7',
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
              ]}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Stack direction='column' alignItems='center'>
                  <Typography
                    variant='b1Bold'
                    sx={{
                      fontSize: '18px !important',
                      lineHeight: '18px !important',
                    }}
                  >
                    {branch.distance}
                  </Typography>
                  <Typography
                    variant='b3Regular'
                    sx={{
                      fontSize: '14px !important',
                      lineHeight: '14px !important',
                    }}
                  >
                    miles
                  </Typography>
                </Stack>
              </Box>

              <Stack direction='column' sx={{ ml: 2, flex: 1 }}>
                <Typography
                  variant='h5'
                  sx={[
                    {
                      textAlign: 'left',
                    },
                    isSelected && { color: 'inherit' },
                  ]}
                  data-testid={`branchName-${branch.branchId}`}
                >
                  {branch.branchName}
                </Typography>
                <Stack direction='row' spacing={0.5} alignItems='center'>
                  <Icon iconName={'location-dot-duotone'} color='primary' />
                  <Typography
                    variant='view-label-secondary'
                    sx={[{ textAlign: 'left', color: 'grey.600' }]}
                    data-testid={`branchAddress-${branch.branchId}`}
                  >
                    {branch.branchAddress}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          );
        })
      )}
    </Stack>
  );
};

export default BranchesSelection;
